import { useEffect, useRef } from "react";
import { resetInitialBlocks } from "@/api/initial-blocks-api";
import { collectionStore } from "./collection-store";
import { mainStreamStore } from "./main-stream-store";
import { queryClient } from "./query-client";
const STALE_THRESHOLD_MS = 5 * 60 * 1000;
/**
 * Watches for the PWA returning from background after ≥5 minutes and
 * refreshes the surfaces whose data lives outside React Query so the
 * user sees new captures without navigating away and back.
 *
 * Three data sources have to be poked, in order:
 *
 *   1. `resetInitialBlocks()` — the homepage's cold-load fetch is a
 *      session-long singleton. Without clearing it, the seed effect
 *      in `mainStreamStore.useList` re-awaits the same resolved
 *      promise and ingests the original cold-load envelopes again —
 *      a "reset" that visually changes nothing.
 *   2. `mainStreamStore.resetPagination()` — wipes the stream's
 *      block-id list / segments and bumps `resetGeneration` so the
 *      seed effect re-fires against the cleared singleton above.
 *   3. `collectionStore.refreshScopeTopPages()` — the per-collection
 *      block index is a custom store, not a React Query cache, so
 *      the `invalidateQueries` below doesn't touch it. This refetches
 *      the top page of every loaded scope (including the collection
 *      the user is currently sitting on) so new blocks appear at
 *      the top.
 *
 * `invalidateQueries` then sweeps the React Query cache (subcollection
 * children, account info, pin lists, etc.) for everything else.
 */
export function useStaleRefresh() {
    const hiddenAtRef = useRef(undefined);
    useEffect(() => {
        function handleVisibilityChange() {
            if (document.visibilityState === "hidden") {
                hiddenAtRef.current = Date.now();
                return;
            }
            const hiddenAt = hiddenAtRef.current;
            hiddenAtRef.current = undefined;
            if (hiddenAt === undefined)
                return;
            const elapsed = Date.now() - hiddenAt;
            if (elapsed < STALE_THRESHOLD_MS)
                return;
            resetInitialBlocks();
            mainStreamStore.resetPagination();
            void collectionStore.refreshScopeTopPages();
            queryClient.invalidateQueries();
        }
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, []);
}
